import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import { divide } from "@aprao/common/src/math/divide.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
  fractionMax,
} from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import type { ShapeLendingStage } from "../shapes/ShapeAppraisal.js";
import type { ShapeInterestLender } from "../shapes/ShapeLender.js";
import type { ShapeCustomCost } from "../shapes/ShapeSitePurchase.js";
import type { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import { LandTaxFacade } from "./LandTaxFacade.js";
import { InterestLenderFacade } from "./lender/InterestLenderFacade.js";
import { ProfitShareLenderFacade } from "./lender/ProfitShareLenderFacade.js";
import type { SalesTaxConfigurationFacade } from "./SalesTaxConfigurationFacade.js";
import type { SitePriceManager } from "./SitePriceManager.js";
import {
  CustomCostFacade,
  type SitePurchaseFacade,
} from "./SitePurchaseFacade.js";

export class ResidualFacade {
  $stores;

  constructor(stores: {
    documentFacade: AppraisalDocumentFacade;
    taxConfigurationFacade: SalesTaxConfigurationFacade | null;
    priceManager: SitePriceManager;
  }) {
    makeAutoObservable(this);

    this.$stores = stores;
  }

  getLandTaxBySite = computedFn(
    (site: SitePurchaseFacade) =>
      new LandTaxFacade(site.$model.landTax, {
        pricePurchase: () =>
          fractionMax(
            FRACTION_ZERO,
            this.$stores.priceManager.getPriceBySite(site) ?? FRACTION_ZERO,
          ),
      }),
  );

  getLandTaxRatioBySite = (site: SitePurchaseFacade) =>
    divide(
      this.getLandTaxBySite(site).landTax,
      fractionMax(
        FRACTION_ZERO,
        this.$stores.priceManager.getPriceBySite(site) ?? FRACTION_ZERO,
      ),
    );

  readonly _decorateCost = computedFn(
    (site: SitePurchaseFacade, cost: ShapeCustomCost) =>
      new CustomCostFacade(cost, {
        ratioPrice: () =>
          fractionMax(
            FRACTION_ZERO,
            this.$stores.priceManager.getPriceBySite(site) ?? FRACTION_ZERO,
          ),
        absolute: () => FRACTION_ONE,
      }),
  );

  getCustomCostsBySite = (site: SitePurchaseFacade) =>
    inferReadonlyArray(
      site.$model.customCosts.map((cost) => this._decorateCost(site, cost)),
    );

  getCustomCostsTotalBySite = (site: SitePurchaseFacade) =>
    sum(
      this.getCustomCostsBySite(site).map((cost) =>
        cost.relativesSet.getRelative("absolute"),
      ),
    );

  getPurchaseCostTotalBySite = (site: SitePurchaseFacade) =>
    sum([
      this.$stores.priceManager.getPriceBySite(site),
      this.getLandTaxBySite(site).landTax,
      this.getCustomCostsTotalBySite(site),
    ]);

  get landTaxesTotal() {
    return sum(
      this.$stores.documentFacade.sitePurchases.map(
        (site) => this.getLandTaxBySite(site).landTax,
      ),
    );
  }

  /**
   * The sum of each individual purchase price.
   */
  get sitePurchasesPriceTotal() {
    return sum(
      this.$stores.documentFacade.sitePurchases.map((site) =>
        this.$stores.priceManager.getPriceBySite(site),
      ),
    );
  }

  /**
   * Ratio of land tax over the sites' net prices.
   */
  get landTaxesTotalRatio() {
    return divide(this.landTaxesTotal, this.sitePurchasesPriceTotal);
  }

  get sitePurchasesCustomCostsTotal() {
    return sum(
      this.$stores.documentFacade.sitePurchases.map((site) =>
        this.getCustomCostsTotalBySite(site),
      ),
    );
  }

  get sitePurchasesCustomCostsTotalRatio() {
    return divide(
      this.sitePurchasesCustomCostsTotal,
      this.sitePurchasesPriceTotal,
    );
  }

  /**
   * The gross total of for all the sites purchases.
   *
   * It includes the net price plus all the tax, fees and custom costs.
   */
  get sitePurchasesCostTotal() {
    return sum(
      this.$stores.documentFacade.sitePurchases.map((site) =>
        this.getPurchaseCostTotalBySite(site),
      ),
    );
  }

  getDevelopmentCostsTotal = (filterFundingType: "debtFunded" | "any") =>
    sum([
      this.sitePurchasesCostTotal,
      this.$stores.documentFacade.getBuildCostTotalWithContingency(
        filterFundingType,
      ),
      this.$stores.documentFacade.otherCostsTotal,
    ]);

  getLenderFacade = computedFn(
    <TLender extends ShapeLendingStage["lenders"][number]>(lender: TLender) => {
      const providers = {
        // the whole reason it's here it's because of this that depends on site price
        debtFundedDevCost: () => this.getDevelopmentCostsTotal("debtFunded"),
        debtFundedGdv: () =>
          this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
            "debtFunded",
          ),
      };

      return (
        lender.loanType === "interest" ?
          new InterestLenderFacade(lender, providers)
        : lender.loanType === "profitShare" ?
          new ProfitShareLenderFacade(lender, providers)
        : assertUnreachable(lender)) as TLender extends ShapeInterestLender ?
        InterestLenderFacade
      : ProfitShareLenderFacade;
    },
  );

  get lenders() {
    return inferReadonlyArray(
      this.$stores.documentFacade.$model.stages.lending.lenders.map((lender) =>
        this.getLenderFacade(lender),
      ),
    );
  }
}
